import type { FooterData } from '@/types/aggregation/common';
import type {
    FooterLegalViewModel,
    FooterLinkItemViewModel,
    FooterLinkSectionViewModel
} from '@/types/layout/footer-view-model';
import type { LinkViewModel } from '@/types/layout/link-view-model';
import { imageUrl, mapGamblingProblemTexts, splitLegalText, toLinkViewModel } from './layout-field-utils';

type FooterSource = Partial<FooterData> | null | undefined;

const mapFooterLinkItem = (item?: {
    link_text?: string;
    link_url?: { url?: string; target?: string };
}): FooterLinkItemViewModel | null => {
    if (!item?.link_text || !item.link_url?.url) {
        return null;
    }

    return {
        linkText: item.link_text,
        linkUrl: toLinkViewModel(item.link_url)
    };
};

export const adaptFooterLinkSections = (source: FooterSource): FooterLinkSectionViewModel[] => {
    const body = source?.body;
    if (!body?.length) {
        return [];
    }

    return body
        .map((slice) => {
            const title = slice?.primary?.category_title;
            if (!title) {
                return null;
            }

            const items = (slice?.items ?? [])
                .map(mapFooterLinkItem)
                .filter((item): item is FooterLinkItemViewModel => item !== null);

            return { title, items };
        })
        .filter((section): section is FooterLinkSectionViewModel => section !== null);
};

export const adaptFooterLegal = (source: FooterSource): FooterLegalViewModel | undefined => {
    if (!source) {
        return undefined;
    }

    return {
        texts:
            source.texts?.map((text) => ({
                legalTexts: splitLegalText(text?.legal),
                gamblingProblemTexts: mapGamblingProblemTexts(text?.gambling_problem)
            })) ?? [],
        productLogoUrl: imageUrl(source.product_logo),
        rgLogoUrl: imageUrl(source.rg_logo),
        rgUrl: toLinkViewModel(source.rg_url),
        stateGcbLogoUrl: imageUrl(source.state_gcb_logo),
        stateGcbUrl: toLinkViewModel(source.state_gcb_url),
        selfExclusionLogoUrl: imageUrl(source.self_exclusion_logo),
        selfExclusionUrl: toLinkViewModel(source.self_exclusion_url),
        paymentOptionsLogoUrl: imageUrl(source.payment_options_logo)
    };
};

const SUPPORT_LINK_LABEL = 'Support';

export const extractSupportLink = (sections: FooterLinkSectionViewModel[]): LinkViewModel | undefined => {
    const supportItem = sections
        .flatMap((section) => section.items)
        .find((item) => item.linkText === SUPPORT_LINK_LABEL);

    if (!supportItem?.linkUrl?.url) {
        return undefined;
    }

    return supportItem.linkUrl;
};
